import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';

interface LeaveSiteConfirmContextValue {
  /** Opens a confirmation dialog; `proceed` runs only if the guest clicks Proceed. */
  requestLeave: (proceed: () => void) => void;
}

const LeaveSiteConfirmContext = createContext<LeaveSiteConfirmContextValue | null>(null);

export function useLeaveSiteConfirm(): LeaveSiteConfirmContextValue {
  const ctx = useContext(LeaveSiteConfirmContext);
  if (!ctx) throw new Error('useLeaveSiteConfirm must be used within LeaveSiteConfirmProvider');
  return ctx;
}

export function LeaveSiteConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<(() => void) | null>(null);

  const requestLeave = useCallback((proceed: () => void) => {
    setPending(() => proceed);
  }, []);

  const handleCancel = () => setPending(null);
  const handleProceed = () => {
    const proceed = pending;
    setPending(null);
    proceed?.();
  };

  return (
    <LeaveSiteConfirmContext.Provider value={{ requestLeave }}>
      {children}
      {pending && (
        <div className="fixed inset-0 z-[120] bg-ink/60 backdrop-blur-[2px] flex items-center justify-center p-4" onClick={handleCancel}>
          <div
            role="alertdialog"
            aria-modal="true"
            className="w-full max-w-[380px] bg-paper rounded-lg border border-hairline shadow-[0_20px_50px_rgba(0,0,0,0.25)] p-6 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-[15px] text-ink leading-relaxed m-0 mb-6">
              This link will take you outside this website. Do you want to continue?
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={handleCancel}
                className="px-5 py-2.5 rounded-full border border-hairline bg-transparent text-ink-soft text-[13.5px] font-semibold cursor-pointer transition-colors hover:border-dark-muted"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleProceed}
                className="px-5 py-2.5 rounded-full bg-olive text-paper text-[13.5px] font-semibold border-none cursor-pointer transition-colors hover:bg-olive-dark"
              >
                Proceed
              </button>
            </div>
          </div>
        </div>
      )}
    </LeaveSiteConfirmContext.Provider>
  );
}
